import { ReadonlyVec3 } from 'gl-matrix';
import { RenderObject } from '../object/RenderObject';
import { RenderGroup } from '../object/RenderGroup';
import { Material, TerrainMaterial } from '../material';
import { DrawableObject, RenderMode } from './index';
import { InstanceGroup } from './InstanceGroup';

/**
 * Per-frame timing accumulator passed into InstanceGroup.updateAndUpload so
 * the per-group sub-phases (LOD partition, matrix copy, GPU upload) all
 * report into one struct on InstanceManager.
 */
export type PrepareStats = {
  partitionMs: number;
  lodPartitionMs: number;
  matrixCopyMs: number;
  bufferGrowMs: number;
  uploadMs: number;
  groupsTouched: number;
  instancesUploaded: number;
};

/**
 * Manages instanced draw groups. Objects sharing the same mesh+material
 * are grouped together and drawn in a single drawElementsInstanced call.
 */
export class InstanceManager {
  private groups = new Map<string, InstanceGroup>();
  private objectToGroup = new Map<RenderObject, InstanceGroup>();
  private gl: WebGL2RenderingContext | null = null;

  // Prepare-frame breakdown stats. Reset at the start of prepareFrame; sub-
  // timers accumulate across the per-group updateAndUpload calls. Mirrors
  // ObjectManager.cullStats — DeferredSceneRenderer reads this after
  // prepareFrame and copies into RenderStats.prepareBreakdown for profiling.
  //
  //  partitionMs       — InstanceManager's outer "visible obj → group" loop
  //  lodPartitionMs    — per-instance distance-band partition in updateAndUpload
  //  matrixCopyMs      — writeMatrices (per-instance modelMatrix → instanceData)
  //  bufferGrowMs      — bufferData() reallocations (rare; capacity grows 1.5x)
  //  uploadMs          — bufferSubData() to GPU
  //  groupsTouched     — count of InstanceGroups updated this frame
  //  instancesUploaded — total instance rows uploaded
  public prepareStats = {
    partitionMs: 0,
    lodPartitionMs: 0,
    matrixCopyMs: 0,
    bufferGrowMs: 0,
    uploadMs: 0,
    groupsTouched: 0,
    instancesUploaded: 0,
  };

  public resetPrepareStats(): void {
    const s = this.prepareStats;
    s.partitionMs = 0;
    s.lodPartitionMs = 0;
    s.matrixCopyMs = 0;
    s.bufferGrowMs = 0;
    s.uploadMs = 0;
    s.groupsTouched = 0;
    s.instancesUploaded = 0;
  }

  /**
   * Check whether an object is eligible for instanced rendering.
   * Must be static, non-animated, non-attached, opaque.
   */
  public isInstanceable(obj: DrawableObject): obj is RenderObject {
    if (!(obj instanceof RenderObject)) return false;
    if (!obj.renderConfiguration.static) return false;
    if (obj.isAnimated) return false;
    if (obj.attachmentIdentity) return false;
    if (obj.boneOverrides && obj.boneOverrides.size > 0) return false;
    // Terrain uses a dedicated shader path with array samplers; it is always
    // unique per scene, so there is no instancing benefit and the group-level
    // GBufferShader binding does not understand TerrainMaterial.
    if (obj.material instanceof TerrainMaterial) return false;
    // Skip transparent objects (they use forward rendering with depth sorting)
    if (obj.material instanceof Material && obj.material.baseColor?.translucent) return false;
    return true;
  }

  /**
   * Register an object for instanced rendering. Returns true if it was grouped.
   */
  public registerObject(obj: DrawableObject): boolean {
    if (!this.isInstanceable(obj)) return false;

    const key = this.getGroupKey(obj);
    let group = this.groups.get(key);
    if (!group) {
      // isInstanceable() guarantees material is not a TerrainMaterial
      const mat = obj.material instanceof Material ? obj.material : undefined;
      group = new InstanceGroup(key, obj.mesh, mat);
      this.groups.set(key, group);
    }
    group.addMember(obj);
    this.objectToGroup.set(obj, group);
    return true;
  }

  /**
   * Unregister an object from instanced rendering.
   */
  public unregisterObject(obj: DrawableObject): void {
    if (!(obj instanceof RenderObject)) return;
    const group = this.objectToGroup.get(obj);
    if (!group) return;
    group.removeMember(obj);
    this.objectToGroup.delete(obj);
    if (group.members.length === 0 && group.renderGroupMembers.size === 0) {
      if (this.gl) group.destroy(this.gl);
      this.groups.delete(group.key);
    }
  }

  /**
   * Register a {@link RenderGroup} for instanced rendering. Unlike
   * `registerObject`, this routes a *cluster* of pre-baked instance matrices
   * to its target `InstanceGroup` in one call — used by tile streamers to
   * scale to scatter populations of 100K+ without paying per-instance hash
   * lookups in `prepareFrame`.
   *
   * The RenderGroup's mesh + material drive the InstanceGroup key, identical
   * to how `registerObject` keys things — so authored statics and scatter
   * RenderGroups that share a mesh+material still draw in one InstanceGroup
   * (one drawElementsInstanced call).
   */
  public registerGroup(group: RenderGroup): void {
    const key = this.getGroupKeyFor(group.mesh.reference, group.material);
    let ig = this.groups.get(key);
    if (!ig) {
      ig = new InstanceGroup(key, group.mesh, group.material);
      this.groups.set(key, ig);
    }
    ig.addGroupMember(group);
    group.targetInstanceGroup = ig;
  }

  /** Unregister a {@link RenderGroup}, tearing down the underlying InstanceGroup if it goes empty. */
  public unregisterGroup(group: RenderGroup): void {
    const ig = group.targetInstanceGroup;
    if (!ig) return;
    ig.removeGroupMember(group);
    group.targetInstanceGroup = null;
    if (ig.members.length === 0 && ig.renderGroupMembers.size === 0) {
      if (this.gl) ig.destroy(this.gl);
      this.groups.delete(ig.key);
    }
  }

  /**
   * Look up the InstanceGroup that owns a freshly-registered RenderGroup.
   * Mirrors `getGroupForObject` for the legacy RenderObject path; used by
   * tile streamers to apply a `ModelLodKit` to fresh InstanceGroups.
   */
  public getGroupForRenderGroup(group: RenderGroup): InstanceGroup | undefined {
    return group.targetInstanceGroup ?? undefined;
  }

  /**
   * Check if an object is managed by the instance manager. Used by
   * `PickingSceneRenderer` to filter scatter (and authored statics) out of
   * the per-object picking pass — picking relies on the depth blit from the
   * G-buffer to occlude pickables behind opaque instance-group geometry.
   *
   * Both legacy RenderObject members and RenderGroup clusters count as
   * managed once they have a target InstanceGroup; only checking
   * `instanceof RenderObject` would let RenderGroups slip through and burn
   * a no-op picking-ID slot per visible cluster every hover frame.
   */
  public isManaged(obj: DrawableObject): boolean {
    if (obj instanceof RenderGroup) return obj.targetInstanceGroup !== null;
    return obj instanceof RenderObject && this.objectToGroup.has(obj);
  }

  /** Look up the InstanceGroup for a given object. */
  public getGroupForObject(obj: DrawableObject): InstanceGroup | undefined {
    return obj instanceof RenderObject ? this.objectToGroup.get(obj) : undefined;
  }

  /** Look up an InstanceGroup by key. */
  public getGroup(key: string): InstanceGroup | undefined {
    return this.groups.get(key);
  }

  /** Iterate all instance groups. */
  public allGroups(): IterableIterator<InstanceGroup> {
    return this.groups.values();
  }

  /**
   * Partition visible objects into instanced groups and non-instanced objects.
   * Uploads instance buffers for groups with visible members.
   *
   * @param cameraPosition Used for LOD distance checks when groups have LOD levels.
   * @param lodDistanceScale Multiplier applied to LOD distance thresholds (1.0 = default).
   */
  public prepareFrame(
    gl: WebGL2RenderingContext,
    visibleObjects: DrawableObject[],
    _mode: RenderMode,
    cameraPosition?: ReadonlyVec3,
    lodDistanceScale = 1.0,
    crossfadeEnabled = false,
  ): {
    instanceGroups: InstanceGroup[];
    nonInstancedObjects: DrawableObject[];
  } {
    this.gl = gl;
    this.resetPrepareStats();
    const stats = this.prepareStats;

    // Clear previous frame's visible sets
    for (const group of this.groups.values()) {
      group.visibleMembers = [];
      group.visibleRenderGroups = [];
    }

    const nonInstancedObjects: DrawableObject[] = [];
    const objectVisible = new Map<InstanceGroup, RenderObject[]>();
    const groupVisible = new Map<InstanceGroup, RenderGroup[]>();

    const partitionStart = performance.now();
    for (const obj of visibleObjects) {
      // RenderGroup path: O(1) routing via targetInstanceGroup, no Map lookup.
      // Critical at scatter scale — the RenderObject branch's `objectToGroup.get`
      // was costing ~2ms/frame at 38K visible instances pre-RenderGroup.
      if (obj instanceof RenderGroup) {
        const ig = obj.targetInstanceGroup;
        if (ig) {
          let list = groupVisible.get(ig);
          if (!list) {
            list = [];
            groupVisible.set(ig, list);
          }
          list.push(obj);
          continue;
        }
      } else if (obj instanceof RenderObject) {
        const ig = this.objectToGroup.get(obj);
        if (ig) {
          let list = objectVisible.get(ig);
          if (!list) {
            list = [];
            objectVisible.set(ig, list);
          }
          list.push(obj);
          continue;
        }
      }
      nonInstancedObjects.push(obj);
    }
    stats.partitionMs = performance.now() - partitionStart;

    // Union of touched InstanceGroups — a group may carry both RenderObjects
    // (authored statics) and RenderGroups (scatter clusters) sharing the same
    // mesh+material. updateAndUpload handles both populations in one pass.
    const touched = new Set<InstanceGroup>();
    for (const ig of objectVisible.keys()) touched.add(ig);
    for (const ig of groupVisible.keys()) touched.add(ig);

    const instanceGroups: InstanceGroup[] = [];
    for (const ig of touched) {
      const objs = objectVisible.get(ig) ?? [];
      const groups = groupVisible.get(ig) ?? [];
      ig.buildInstanceVAO(gl);
      ig.updateAndUpload(gl, objs, groups, cameraPosition, lodDistanceScale, crossfadeEnabled, stats);
      instanceGroups.push(ig);
      stats.groupsTouched++;
      let groupInstances = 0;
      for (const g of groups) groupInstances += g.instanceCount;
      stats.instancesUploaded += objs.length + groupInstances;
    }

    return { instanceGroups, nonInstancedObjects };
  }

  public destroy(gl: WebGL2RenderingContext): void {
    for (const group of this.groups.values()) {
      group.destroy(gl);
    }
    this.groups.clear();
    this.objectToGroup.clear();
  }

  private getGroupKey(obj: RenderObject): string {
    // isInstanceable() has already filtered out TerrainMaterial, so the cast
    // here is safe — only standard Material instances reach this point.
    const mat = obj.material instanceof Material ? obj.material : undefined;
    return this.getGroupKeyFor(obj.mesh.reference, mat);
  }

  /**
   * Compute the InstanceGroup cache key from a mesh reference + material.
   * Shared by `registerObject` (RenderObject path) and `registerGroup`
   * (RenderGroup path) so authored statics and scatter clusters that share
   * a mesh+material collapse into the same draw call.
   */
  private getGroupKeyFor(meshRef: string | number, mat: Material | undefined): string {
    const baseTexId = mat?.baseColor?.glTexture ? String(mat.baseColor.glTexture) : 'none';
    const matColor = mat?.color ? Array.from(mat.color).join(',') : 'default';
    return `${meshRef}|${baseTexId}|${matColor}`;
  }
}
